import { isError } from 'lodash';
import { assign, createMachine } from 'xstate';
import { choose } from 'xstate/lib/actions';
import { BUSINESS } from '~/funnels/business/config';
import { COMMUNITY_SOLAR_FUNNEL_ID } from '~/funnels/community-solar/config';
import { GO_SOLAR_FUNNEL_ID } from '~/funnels/go-solar/config';
import { I_SOLAR_UCB } from '~/funnels/isolar-ucb/config';
import { isPowerSetter } from '~/funnels/powersetter/machine';
import { PARTNER_PROVIDED, BILL_CAPTURE } from '~/funnels/resuming/config';
import { SOLAR_LANDSCAPE_FUNNEL_ID } from '~/funnels/solar-landscape/config';
import { billingMachine, billingContext } from '~/machines/billing-selection';
import { docuSignContext, docuSignMachine } from '~/machines/docusign';
import { enrollmentMachine, enrollmentContext } from '~/machines/enrollment-selection';
import { legalMachine, legalContext, LegalContext } from '~/machines/legal-completion';
import { reviewMachine, reviewContext } from '~/machines/review-form';
import { uploadMachine, uploadContext, UploadContext } from '~/machines/upload';
import { uploadContractsContext, uploadContractsMachine } from '~/machines/upload-contracts';
import { userContext, userMachine } from '~/machines/user-creation';
import { utilityContext, utilityMachine } from '~/machines/utility-selection';
import { LDClient, LDFlags } from '~/providers';
import {
  fetchBillingExperienceOptions,
  redirectToDashboard,
  signUpUser,
  sendDocusignInstantAlert,
  fetchExistingPlans,
  fetchWaitlistOptions,
  updateOriginationSource,
  fetchExistingUtilityAccounts,
  createPaymentHold,
  listenForPayment,
  fetchFunnelPacket,
  updateConsolidatedUtilityAccount,
  redirectToDashboardFromDocusignTimeout,
  updateUtilityAccountDetail,
} from '~/services/';

import { BillingDataSource, EnrollmentDataSource } from '~/typings/context';
import { ContractTag, FunnelTrack, PaymentHoldResult, PaymentMethodType } from '~/typings/graphql';
import {
  BlockedStates,
  BLOCKED_STATES_FOR_ENERGYWELL,
  DOCU_SIGN_LMI_STATES,
  LMI_POWERFORM_URL,
  PROMO_CODES,
  UTILITY_IDS,
} from '~/utils/constants';
import { removeKeys } from '~/utils/for-warbler';
import { getEnrollmentOptions } from '~/utils/get-enrollment-options';
import { communitySolarFunnelRedirects } from '~/utils/redirects';
import { store } from '~/utils/store';
import {
  allowConsolidatedUtilityAccount,
  getUserIpFromStore,
  identify,
  sendToSegment,
  trackError,
} from '~/utils/tracking';
import { acuityContext, acuityMachine } from '../acuity';
import { lmiReviewMachine } from '../lmi-review';
import { PerchEnergyContext } from '../perch-energy/defs/types';
import { docuSignUseCase, needsDocuSign, usesIpaContracts } from '../utils/docusign';
import {
  SolarFunnelContext,
  SolarFunnelEvent,
  SolarFunnelParams,
  SolarFunnelServices,
} from './types';

export const solarMachine = (params: SolarFunnelParams) =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as SolarFunnelContext,
        events: {} as SolarFunnelEvent,
        services: {} as SolarFunnelServices,
      },
      context: {
        queryParams: {},
        forceWaitlist: false,
      },
      id: params.id,
      entry: 'updateTrackingIdentity',
      initial: params.initialState || 'start',
      states: {
        start: {
          entry: ['setFunnelId'],
          always: [
            { cond: 'isBlockSignupWhenResuming', target: 'csUnavailable' },
            {
              cond: 'isResuming',
              target: 'fetchWaitlistOptions',
              actions: ['setResumingData', 'updateTrackingIdentity'],
            },
            {
              cond: 'isResumingWithLmi',
              target: 'fetchWaitlistOptions',
              actions: ['setResumingData', 'updateTrackingIdentity'],
            },
            { target: 'utility' },
          ],
        },
        utility: {
          invoke: {
            id: 'utilityMachine',
            src: 'utilityMachine',
            data: utilityContext(funnelContext => ({
              queryParams: funnelContext.queryParams,
              redirect: communitySolarFunnelRedirects,
              resuming: funnelContext.queryParams.resumingUser,
              funnelId: params.id,
              ...params?.utility?.context,
            })),
            onDone: {
              target: 'fetchFeatureFlags',
              actions: [
                'setUtilitySelection',
                'addUtilitySelectionToStore',
                'setFunnelVnmProjectId',
              ],
            },
          },
        },
        fetchFeatureFlags: {
          invoke: {
            id: 'updateTrackingIdentity',
            src: 'updateTrackingIdentity',
            onDone: [
              {
                cond: 'isSignupUnavailable',
                target: 'csUnavailable',
              },

              { target: 'user' },
            ],
            onError: { target: 'user', actions: 'trackFetchFeatureFlagsError' },
          },
        },
        user: {
          invoke: {
            id: 'userMachine',
            src: 'userMachine',
            data: userContext(funnelContext => {
              return {
                funnelId: params.id,
                queryParams: funnelContext.queryParams,
                requirePhone: funnelContext.utilitySelection?.state.abbreviation === 'IL',
                utility: funnelContext.utilitySelection!,
                zipCode: funnelContext.zipCode!,
                funnelVnmProjectId:
                  funnelContext.funnelVnmProjectId! ||
                  LDClient.variation(LDFlags.ffDefaultSignupProjectId)?.project_id,
                forceWaitlist: funnelContext.forceWaitlist!,
                promoCode:
                  funnelContext.queryParams?.promoCode ||
                  funnelContext.queryParams?.unparsedQueryParams?.promoCode,
                ...params?.user?.context,
              };
            }),
            onDone: [
              {
                cond: 'energywellShouldHoldPayment',
                target: 'listenForPaymentCreation',
                actions: 'setFunnelPacket',
              },
              {
                cond: 'isEnergyWell',
                target: 'enrollment',
                actions: 'setFunnelPacket',
              },
              {
                cond: 'isPowerSetter',
                target: 'enrollment',
                actions: 'setFunnelPacket',
              },
              {
                cond: 'isWaitlistEnabled', // temporarily needed for waitlist
                target: 'fetchWaitlistOptions',
                actions: 'setFunnelPacket',
              },
              {
                cond: 'isUserFromNY',
                target: 'getEnrollmentOptions',
                actions: 'setFunnelPacket',
              },
              {
                cond: 'isUserFromVA',
                target: 'getEnrollmentOptions',
                actions: 'setFunnelPacket',
              },
              {
                cond: 'isUserFromMD',
                target: 'getEnrollmentOptions',
                actions: 'setFunnelPacket',
              },
              {
                cond: 'isMinnesotaLMI',
                target: 'getEnrollmentOptions',
                actions: 'setFunnelPacket',
              },
              {
                cond: 'shouldUploadDocs',
                target: 'upload',
                actions: 'setFunnelPacket',
              },
              {
                cond: 'requiresAffirmativeConsent',
                target: 'updateUtilityAccountDetail',
                actions: 'setFunnelPacket',
              },
              {
                target: 'getEnrollmentOptions',
                actions: 'setFunnelPacket',
              },
            ],
          },
          on: {
            CHANGE_UTILITY: { target: 'utility', actions: 'clearUtilitySelection' },
          },
        },
        listenForPaymentCreation: {
          id: 'listenForPaymentCreation',
          initial: 'waiting',
          states: {
            waiting: {
              after: {
                LISTEN_FOR_PAYMENT_TIMEOUT: 'timeout',
              },
              invoke: {
                id: 'listenForPaymentCreation',
                src: 'listenForPaymentCreation',
                onDone: [
                  {
                    actions: 'setEnergywellPaymentMethod',
                    target: '#verifyPaymentType',
                  },
                ],
                onError: '#unexpected',
              },
            },
            timeout: {
              always: { target: '#paymentVerificationFailed' },
            },
          },
        },
        verifyPaymentType: {
          id: 'verifyPaymentType',
          always: [
            {
              cond: 'isPaymentTypeCard',
              target: 'verifyPaymentHold',
            },
            {
              target: '#enrollment',
            },
          ],
        },
        verifyPaymentHold: {
          id: 'verifyPaymentHold',
          invoke: {
            src: 'verifyPaymentHold',
            onDone: [
              {
                cond: 'paymentHoldFailed',
                target: '#paymentVerificationFailed',
                actions: ['setPaymentHoldResult', 'handlePaymentHoldError'],
              },
              { target: '#enrollment' },
            ],
            onError: '#unexpected',
          },
        },
        paymentVerificationFailed: {
          id: 'paymentVerificationFailed',
          on: {
            CONTINUE_CLICKED: '#enrollment',
          },
        },
        fetchWaitlistOptions: {
          invoke: {
            id: 'fetchWaitlistOptions',
            src: 'fetchWaitlistOptions',
            onDone: [
              {
                target: 'waitlistDecider',
                actions: 'setWaitlistParams',
              },
            ],
            onError: 'unexpected',
          },
        },
        waitlistDecider: {
          entry: choose([
            {
              cond: 'isUtilityInWaitList',
              actions: 'updateOriginationSource',
            },
          ]),
          always: [
            {
              cond: 'isUserFromNY',
              target: 'getEnrollmentOptions',
            },
            {
              cond: 'isUserFromVA',
              target: 'getEnrollmentOptions',
            },
            {
              cond: 'isMinnesotaLMI',
              target: 'getEnrollmentOptions',
            },
            {
              cond: 'isUserFromMD',
              target: 'getEnrollmentOptions',
            },
            {
              // On resuming the agent should start with the enrollment step
              cond: 'isEnergyWell',
              target: 'enrollment',
            },
            {
              // On resuming the agent should start with the enrollment step
              cond: 'isPowerSetter',
              target: 'enrollment',
            },
            {
              cond: 'shouldUploadDocs',
              target: 'upload',
            },
            {
              cond: 'shouldSkipToLegal',
              target: 'legal',
            },
            {
              cond: 'shouldWaitlistSkipPayment',
              target: 'getEnrollmentOptions',
            },
            {
              cond: 'requiresAffirmativeConsent',
              target: 'updateUtilityAccountDetail',
            },
            {
              target: 'getEnrollmentOptions',
            },
          ],
        },
        upload: {
          id: 'upload',
          invoke: {
            id: 'uploadMachine',
            src: 'uploadMachine',
            data: uploadContext(funnelContext => {
              const shouldEnableSkipForMD =
                isUserFromMD() && funnelContext.lmiZoneOptions?.isInCensusTract;
              const uploadContext = {
                funnelPacket: funnelContext.funnelPacket!,
                promoCode:
                  funnelContext.queryParams?.promoCode ||
                  funnelContext.queryParams?.unparsedQueryParams?.promoCode,
                utmParams: funnelContext.queryParams?.utmParams,
                shouldEnableSkip: isUserFromMD() ? shouldEnableSkipForMD : undefined,
                funnelId: funnelContext.funnelId || params.id,
              } as UploadContext;
              return uploadContext;
            }),
            onDone: [
              {
                cond: 'isEnergyWell',
                target: 'uploadContracts',
                actions: 'setBillingSelectionAsDual',
              },
              {
                cond: 'isPowerSetter',
                target: 'uploadContracts',
                actions: 'setBillingSelectionAsUcb',
              },
              /**
               * if the user is a NY user and transition to this upload state,
               * that means we've already gotten all their info and redirected
               * them here because they are neither in DAC nor in Empower Zone.
               * So the next step is legal.
               */
              { cond: 'isUserInNonLmiUtility', target: 'legal' },
              { cond: 'isNYUserNeitherDacNorEZ', target: 'legal' },
              { cond: 'isNYUserLMIZoneOptionUnknown', target: 'legal' },
              { cond: 'isUserFromVA', target: 'legal' },
              { cond: 'isUserFromMD', target: 'legal' },
              { target: 'getEnrollmentOptions' },
            ],
          },
        },
        uploadContracts: {
          invoke: {
            id: 'uploadContractsMachine',
            src: 'uploadContractsMachine',
            data: uploadContractsContext(funnelContext => {
              return {
                billingSelection: funnelContext?.billingSelection!,
                funnelPacket: funnelContext?.funnelPacket!,
                paymentMethod: funnelContext?.paymentMethod,
                utmParams: funnelContext?.queryParams.utmParams,
                externalSystem: getExternalSystem(funnelContext),
                ...params?.legal?.context,
              };
            }),
            onDone: { target: 'legal' },
          },
        },
        getEnrollmentOptions: {
          entry: [
            assign((_context: SolarFunnelContext) => {
              const enrollmentOptions = getEnrollmentOptions({
                preference: _context.queryParams.preferredEnrollment,
                limit: LDClient.variation(LDFlags.showFallbackButton) ? undefined : 1,
                funnelId: params.id,
                utilityId: _context.funnelPacket!.utility.id,
              });
              return enrollmentOptions;
            }),
          ],
          always: [
            {
              cond: 'isUanPresent',
              target: 'enrollment',
            },
            {
              cond: 'isMnLmiWithSelfAttestation',
              target: 'legal',
            },
            {
              cond: 'isMnLmiWithAddress',
              target: 'lmiReview',
            },
            {
              cond: 'isMnLmiWithoutAddress',
              target: 'enrollment',
            },
            {
              cond: 'shouldAdvanceOnResumingWithUtilityConnect',
              target: 'fetchBillingExperienceOptions',
              actions: 'setEnrollmentSelectionAsUtilityConnect',
            },
            {
              cond: 'shouldAdvanceOnResumingWithUtilityAccountNumber',
              target: 'fetchBillingExperienceOptions',
              actions: 'setEnrollmentSelectionAsPartnerProvided',
            },
            { target: 'enrollment' },
          ],
        },
        updateUtilityAccountDetail: {
          invoke: {
            id: 'updateUtilityAccountDetail',
            src: 'updateUtilityAccountDetail',
            onDone: {
              target: 'getEnrollmentOptions',
            },
            onError: 'unexpected',
          },
        },
        enrollment: {
          id: 'enrollment',
          invoke: {
            id: 'enrollmentMachine',
            src: 'enrollmentMachine',
            data: enrollmentContext(funnelContext => ({
              funnelPacket: funnelContext.funnelPacket!,
              funnelId: params.id,
              enrollmentOptions: funnelContext.enrollmentOptions,
              showUtilityConnectButton:
                funnelContext?.enrollmentOptions?.includes('utility_connect'),
              isUserMissingServiceAddress: funnelContext.isUserMissingServiceAddress,
              startAtAccountNumber:
                funnelContext.queryParams?.enrollmentOption === PARTNER_PROVIDED ||
                LDClient.variation(LDFlags.showUtiltityAccountNumberScreen) ||
                isEnergyWell(funnelContext) ||
                isPowerSetter(funnelContext),
              startAtBillUpload: funnelContext.queryParams?.enrollmentOption === BILL_CAPTURE,
              promoCode:
                funnelContext.queryParams?.promoCode ||
                funnelContext.queryParams?.unparsedQueryParams?.promoCode,
              queryParams: funnelContext.queryParams,
              unparsedQueryParams: funnelContext.queryParams?.unparsedQueryParams,
              utmParams: funnelContext.queryParams?.utmParams,
              advanceOn: ['verified', 'timed_out'],
              ...params?.enrollment?.context,
            })),
            onDone: [
              {
                cond: 'isEnergyWell',
                target: 'upload',
                actions: ['setEnrollmentSelection'],
              },
              {
                cond: 'isPowerSetter',
                target: 'upload',
                actions: ['setEnrollmentSelection'],
              },
              {
                cond: 'isMinnesotaLMI',
                target: 'lmiReview',
                actions: [
                  'setUtilityConnectStatus',
                  'setBillingSelectionAsUcb',
                  'setEnrollmentSelection',
                ],
              },
              // Skip billing incase the funnel track is waitlist
              { cond: 'shouldWaitlistSkipPayment', target: 'legal' },
              {
                target: 'refetchFunnelPacket',
                actions: [
                  'setEnrollmentSelection',
                  'setNyLmiZoneOptions',
                  'setIsInLmiArea',
                  'setUtilityConnectStatus',
                ],
              },
            ],
          },
        },
        refetchFunnelPacket: {
          id: 'refetchFunnelPacket',
          invoke: {
            id: 'refetchFunnelPacket',
            src: 'refetchFunnelPacket',
            onDone: {
              target: 'fetchBillingExperienceOptions',
              actions: 'saveFunnelPacket',
            },
          },
        },
        fetchBillingExperienceOptions: {
          invoke: {
            id: 'fetchBillingExperienceOptions',
            src: 'fetchBillingExperienceOptions',
            onDone: [
              {
                cond: 'isPowerSetter',
                target: 'upload',
              },
              {
                cond: 'isConstellation',
                target: 'legal',
                actions: ['setBillingSelectionAsUcb', 'setBillingExperienceOptions'],
              },
              {
                cond: 'onlyUcbVAUtility',
                target: 'upload',
                actions: ['setBillingSelectionAsUcb', 'setBillingExperienceOptions'],
              },
              {
                cond: 'isUserFromOregonLmi',
                target: 'acuity',
                actions: ['setBillingSelectionAsUcb', 'setBillingExperienceOptions'],
              },
              {
                cond: 'isNjUtility',
                target: 'review',
                actions: ['setBillingSelectionAsUcb', 'setBillingExperienceOptions'],
              },
              {
                cond: 'onlyUcbNY',
                target: 'lmiZoneVerification',
                actions: ['setBillingSelectionAsUcb', 'setBillingExperienceOptions'],
              },
              {
                cond: 'onlyUcb',
                target: 'legal',
                actions: ['setBillingSelectionAsUcb', 'setBillingExperienceOptions'],
              },
              {
                /**
                 * if user is missing service address, they
                 * already were at payment, sent back to fallback
                 * and then again coming here after filling their
                 * details manually. We must skip payment
                 * screen for such users as they already had given
                 * their payment detail.
                 */
                cond: 'isPaymentMethodAlreadySelected',
                target: 'legal',
                actions: ['setBillingSelectionAsDual'],
              },
              {
                target: 'billingDecider',
                actions: ['setBillingExperienceOptions', 'setUcbAvailableAsOption'],
              },
            ],
            onError: 'unexpected',
          },
        },
        billingDecider: {
          entry: 'trackBillingDecider',
          always: [
            {
              cond: 'isTelesalesNoPayment',
              target: 'noBilling',
            },
            {
              target: 'billing',
            },
          ],
        },
        noBilling: {
          entry: ['trackNoBilling', 'setBillingSelectionAsUcb'],
          always: [
            {
              target: 'postBillingSelection',
            },
          ],
        },
        billing: {
          invoke: {
            id: 'billingMachine',
            src: 'billingMachine',
            data: billingContext(funnelContext => ({
              billingSelection: funnelContext?.billingSelection,
              billingOptions: funnelContext.billingExperienceOptions,
              isUcbAvailableAsOption: funnelContext.isUcbAvailableAsOption,
              utmParams: funnelContext.queryParams.utmParams,
              funnelId: params.id,
              funnelPacket: funnelContext.funnelPacket!,
              ...params?.billing?.context,
            })),
            onDone: [{ target: 'postBillingSelection', actions: 'setBillingSelection' }],
          },
        },
        postBillingSelection: {
          always: [
            {
              cond: 'isResumingNjUtility',
              target: 'review',
            },
            {
              cond: 'isUserFromNY',
              target: 'lmiZoneVerification',
            },
            {
              cond: 'isUserFromMD',
              target: 'lmiZoneVerification',
            },
            {
              cond: 'isUserFromDE',
              target: 'lmiZoneVerification',
            },
            {
              cond: 'isUserMissingServiceAddress',
              target: 'enrollment',
              actions: ['setisUserMissingServiceAddress'],
            },
            { target: 'legal' },
          ],
        },
        lmiZoneVerification: {
          always: [
            {
              cond: 'isUserInNonLmiUtility',
              target: 'upload',
            },
            {
              cond: 'isUserInDacZone',
              target: 'review',
            },
            {
              cond: 'isUserInEmpowerZone',
              target: 'legal',
            },
            {
              /* For cases where the scraper has not returned and address is missing
               we will be showing self attestaion screen followed by proof doc upload
               screen to capture the data
               */
              cond: 'isNYUserLMIZoneOptionUnknown',
              target: 'review',
            },
            {
              cond: 'isUserFromMD',
              target: 'review',
            },
            {
              cond: 'isUserFromDE',
              target: 'review',
            },
            { target: 'upload' },
          ],
        },
        review: {
          invoke: {
            id: 'reviewMachine',
            src: 'reviewMachine',
            // @ts-expect-error - TS doesn't like the context function here
            data: reviewContext(funnelContext => {
              return {
                billingSelection: funnelContext.billingSelection!,
                funnelPacket: funnelContext.funnelPacket!,
                paymentMethod: funnelContext.paymentMethod,
                utmParams: funnelContext.queryParams.utmParams,
                ...params?.legal?.context,
              };
            }),
            onDone: [
              {
                cond: 'isNonLmi',
                target: 'legal',
                actions: 'setDocusignContracts',
              },
              {
                target: 'docuSign',
                actions: ['setDocusignContracts', 'setSelfAttestationData'],
              },
            ],
          },
        },
        acuity: {
          invoke: {
            id: 'acuityMachine',
            src: 'acuityMachine',
            data: acuityContext(funnelContext => {
              return {
                billingSelection: funnelContext.billingSelection,
                funnelPacket: funnelContext.funnelPacket!,
                paymentMethod: funnelContext.paymentMethod,
                utmParams: funnelContext.queryParams.utmParams,
                ...params?.legal?.context,
              };
            }),
            onDone: 'legal',
          },
        },
        lmiReview: {
          invoke: {
            id: 'lmiReviewMachine',
            src: 'lmiReviewMachine',
            data: context => ({
              funnelPacket: context.funnelPacket!,
              ...params?.legal?.context,
            }),
            onDone: [
              {
                cond: 'isNonLmiAndUtilityConnect',
                target: 'legal',
                actions: ['setDocusignContracts', 'setSelfAttestationData'],
              },
              {
                target: 'docuSign',
                actions: ['setDocusignContracts', 'setSelfAttestationData'],
              },
            ],
          },
        },
        legal: {
          invoke: {
            id: 'legalMachine',
            src: 'legalMachine',
            data: legalContext(funnelContext => {
              return {
                billingSelection: funnelContext.billingSelection!,
                funnelPacket: funnelContext.funnelPacket!,
                paymentMethod: funnelContext.paymentMethod,
                utmParams: funnelContext.queryParams?.utmParams,
                funnelId: funnelContext.funnelId || params.id,
                /**
                 * We need the below condition for not showing the LMI options for the users and since the
                 flag is a bit sporadic and is affected only after returning from docusign which would be
                 the case for dac zone ,to override the flag we are using a dac zone check
                 * FOR DAC - if flag = false , funnelContext.nyLmiZoneOptions?.dacZone will be false -> returns false
                 * FOR DAC - if flag = true , funnelContext.nyLmiZoneOptions?.dacZone will be true -> returns true
                 * FOR NON DAC or Empowerzone - if flag = true , funnelContext.nyLmiZoneOptions?.dacZone will be false ->  returns true
                 */
                isNYLMI:
                  LDClient.variation(LDFlags.shouldUseNYLmiNewFlow) ||
                  funnelContext.nyLmiZoneOptions?.dacZone,
                forceWaitlist: funnelContext.forceWaitlist!,
                isEnergyWell: isEnergyWell(funnelContext),
                isPowerSetter: isPowerSetter(funnelContext),
                promoCode: funnelContext.queryParams?.promoCode,
                contractTags: requiresAffirmativeConsent(funnelContext)
                  ? [ContractTag.NjAffirmative]
                  : undefined,
                ...params?.legal?.context,
              };
            }),
            onDone: [
              {
                cond: 'isResumingNjUtility',
                target: 'completed',
                actions: 'setSelfAttestationData',
              },
              {
                cond: 'isNjUtility',
                target: 'completed',
                actions: 'setSelfAttestationData',
              },
              {
                target: 'completed',
                actions: ['setDocusignContracts', 'setSelfAttestationData'],
              },
            ],
          },
        },
        docuSign: {
          id: 'docuSign',
          invoke: {
            id: 'docuSignMachine',
            src: 'docuSignMachine',
            data: docuSignContext(funnelContext => {
              const { funnelPacket, queryParams, docuSignContracts } = funnelContext;
              const signedStatus = queryParams?.docusignUser?.signedStatus;
              return {
                selfAttestationData: funnelContext.selfAttestationData,
                contracts: docuSignContracts ?? [],
                funnelPacket: funnelPacket!,
                straightToTimeout:
                  usesIpaContracts(funnelContext) &&
                  !LDClient.variation(LDFlags.embeddedSigningIpa) &&
                  !signedStatus,
                useCase: docuSignUseCase(funnelContext),
                userId: funnelPacket!.user.id,
                utilityAccountId: funnelPacket!.utilityAccount.id,
                utmParams: queryParams.utmParams,
                signedStatus,
                promoCode: queryParams.promoCode || queryParams.unparsedQueryParams?.promoCode,
              };
            }),
            onDone: [
              {
                cond: 'shouldDocusignTimeoutRedirectToDashboard',
                target: 'docusignTimeoutRedirect',
              },
              {
                cond: 'isResumingNjUtility',
                target: 'legal',
              },
              {
                cond: 'isNjUtility',
                target: 'legal',
              },
              {
                cond: 'isMinnesotaLMI',
                target: 'legal',
              },
              {
                cond: 'isNyLmiOptionsUnknown',
                target: 'upload',
              },
              {
                cond: 'isNyDacUser',
                target: 'legal',
              },
              {
                cond: 'isUserFromMD',
                target: 'upload',
              },
              {
                cond: 'isUserFromDE',
                target: 'legal',
              },
              {
                target: 'completed.fetchExistingPlans',
              },
            ],
          },
        },
        docusignTimeoutRedirect: {
          id: 'docusignTimeoutRedirect',
          invoke: {
            id: 'docusignTimeoutRedirect',
            src: 'docusignTimeoutRedirect',
            onDone: { target: 'completed' },
            onError: { target: 'completed' },
          },
        },
        completed: {
          id: 'completed',
          entry: 'trackFunnelCompleted',
          initial: 'assigning',
          states: {
            assigning: {
              id: 'assigning',
              invoke: {
                id: 'signUpUser',
                src: 'signUpUser',
                onDone: [
                  { cond: 'shouldWaitlistSkipPayment', target: 'fetchExistingPlans' },
                  { cond: 'isNjUtility', target: 'fetchExistingPlans' },
                  { cond: 'isMinnesotaLMI', target: 'fetchExistingPlans' },
                  { cond: 'isResumingNjUtility', target: 'fetchExistingPlans' },
                  { cond: 'hasNoUtilityAccountsAdded', target: 'fetchExistingPlans' },
                  { cond: 'needsDocuSign', target: '#docuSign' },
                  { cond: 'isLmi', target: '#lmiRedirect' },
                  { target: 'fetchExistingPlans' },
                ],
                onError: '#unexpected',
              },
            },
            lmiRedirect: {
              id: 'lmiRedirect',
              type: 'final',
              onEntry: 'redirectToLmiDocusign',
            },
            fetchExistingPlans: {
              invoke: {
                id: 'fetchExistingPlans',
                src: 'fetchExistingPlans',
                onDone: {
                  target: 'sendInstantAlert',
                  actions: 'setExistingPlans',
                },
                onError: { target: 'sendInstantAlert' },
              },
            },
            sendInstantAlert: {
              id: 'sendInstantAlert',
              invoke: {
                id: 'sendInstantAlert',
                src: 'sendInstantAlert',
                onDone: { target: 'redirecting' },
                onError: { target: 'redirecting' },
              },
            },
            redirecting: {
              id: 'redirecting',
              type: 'final',
              onEntry: 'redirectToDashboard',
            },
          },
        },
        unexpected: {
          id: 'unexpected',
        },
        csUnavailable: {
          id: 'csUnavailable',
        },
      },
    },
    {
      actions: {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        // TLDR: need the unused _context - https://xstate.js.org/docs/guides/typescript.html#troubleshooting
        clearUtilitySelection: assign(context => ({
          utilitySelection: undefined,
          queryParams: removeKeys(context.queryParams, ['utilityId', 'zipCode']),
        })),
        // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
        redirectToDashboard: context => {
          const planId = context.funnelPacket!.customPlan.id;
          const utilityAccountId = context.funnelPacket!.utilityAccount.id;
          const existingPlans = context.existingPlans!;
          const plans = existingPlans.filter(customPlan => customPlan.active === true);
          const isWaitlist = isFunnelTrackWaitlist(context);
          let path: string | undefined;
          if (isWaitlist) {
            path = '/dashboard';
          } else if (plans.length === 1) {
            path = `/dashboard/${planId}/solar`;
          }
          const params = {
            userId: context.funnelPacket!.user.id,
            utilityAccountId,
            billingExperienceType: context.billingSelection,
            ...(path ? { path } : {}),
          };
          redirectToDashboard(params);
        },
        redirectToLmiDocusign: () => {
          const url = LMI_POWERFORM_URL;
          if (url) window.location.assign(url);
        },
        // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
        // @ts-expect-error TODO fix the typegen
        setFunnelId: assign((_context, _event) => ({
          funnelId: params.id,
        })),
        setBillingExperienceOptions: assign((_context, event) => ({
          billingExperienceOptions: event.data.billingExperienceOptions,
        })),
        setExistingPlans: assign((_context, event) => {
          return { existingPlans: event.data.existingPlans };
        }),
        setUcbAvailableAsOption: assign((_context, event) => ({
          isUcbAvailableAsOption: event.data.billingExperienceOptions.includes('ucb'),
        })),
        setBillingSelection: assign((_context, event) => ({
          billingSelection: event.data.billingSelection,
          paymentMethod: event.data.selectedPaymentMethod,
        })),
        setisUserMissingServiceAddress: assign((_context, _event) => ({
          isUserMissingServiceAddress: true,
        })),
        setSelfAttestationData: assign((_context, event) => ({
          selfAttestationData: event.data?.selfAttestationData,
        })),
        setBillingSelectionAsUcb: assign(_context => ({
          billingSelection: 'ucb' as BillingDataSource,
        })),
        setBillingSelectionAsDual: assign(_context => ({
          billingSelection: 'dual' as BillingDataSource,
        })),
        setEnrollmentSelection: assign((_context, event) => ({
          enrollmentSelection: event.data.enrollmentSelection,
        })),
        setNyLmiZoneOptions: assign((_context, event) => ({
          nyLmiZoneOptions: event?.data?.nyLmiZoneOptions,
        })),
        setIsInLmiArea: assign((_context, event) => ({
          isInLmiArea: event?.data?.isInLmiZone,
          lmiZoneOptions: event?.data?.lmiZoneOptions,
        })),
        setUtilityConnectStatus: assign((_context, event) => ({
          utilityConnectStatus: event?.data?.utilityConnectStatus,
        })),
        setEnrollmentSelectionAsUtilityConnect: assign(_context => ({
          enrollmentSelection: 'utility_connect' as EnrollmentDataSource,
        })),
        setEnrollmentSelectionAsPartnerProvided: assign(_context => ({
          enrollmentSelection: 'partner_provided' as EnrollmentDataSource,
        })),
        setFunnelPacket: assign((_context, event) => {
          return {
            funnelPacket: event.data.funnelPacket,
            utmParams: event.data.utmParams,
          };
        }),
        setDocusignContracts: assign((_context, event) => ({
          docuSignContracts: event.data?.docuSignContracts,
          lmiStatus: event.data?.lmiStatus,
        })),
        setFunnelVnmProjectId: assign((_context, event) => ({
          funnelVnmProjectId:
            event.data.funnelVnmProjectId ||
            LDClient.variation(LDFlags.ffDefaultSignupProjectId)?.project_id,
        })),
        setUtilitySelection: assign((_context, event) => ({
          utilitySelection: event.data.utility,
          zipCode: event.data.zipCode,
          forceWaitlist: false,
        })),
        setWaitlistParams: assign((_context, event) => {
          return {
            utilityScrapeEligible: event.data?.utilityScrapeEligible,
            funnelTrack: event.data?.funnelTrack,
          };
        }),
        setEnergywellPaymentMethod: assign((_context, event) => ({
          energywellPaymentMethod: event.data.paymentMethod,
        })),
        setPaymentHoldResult: assign((_context, event) => ({
          paymentHoldResult: event?.data?.result as PaymentHoldResult,
          paymentHoldMessage: event?.data?.message,
        })),
        setResumingData: assign(context => ({
          funnelPacket: context.queryParams.resumingUser!.packet,
          nyLmiZoneOptions: context.queryParams.resumingUser!.nyLmiZoneOptions,
          isInLmiArea: context.queryParams.resumingUser!.isInLmiArea,
          forceWaitlist: context.queryParams.resumingUser!.forceWaitlist,
          utmParams: context.queryParams.utmParams,
          userDocusignEnvelopes: context.queryParams.resumingUser!.userDocusignEnvelopes,
        })),
        saveFunnelPacket: assign((_context, event) => ({
          funnelPacket: event.data.funnelPacket,
        })),
        addUtilitySelectionToStore: (_context, event) =>
          store.set('UTILITY_SELECTION', event.data.utility),
        trackFunnelCompleted: context => {
          sendToSegment({
            type: 'funnel_completed',
            funnelPacket: context.funnelPacket!,
            track: context.utilitySelection?.funnelTrack,
          });
        },
        trackBillingDecider: (context, _event) => {
          sendToSegment({
            type: 'track_billing_decider_step_started',
            funnelPacket: context.funnelPacket!,
          });
        },
        trackNoBilling: (context, _event) => {
          sendToSegment({
            type: 'track_no_billing_step_started',
            funnelPacket: context.funnelPacket!,
          });
        },
        trackFetchFeatureFlagsError: (context, event: { data?: unknown }) => {
          sendToSegment({
            type: 'track_ld_fetch_feature_flags_error',
            funnelTrack: context.utilitySelection?.funnelTrack,
            utilityId: context.utilitySelection?.id,
            errorClass: !!event.data && isError(event.data) ? event.data.name : 'LD_Down',
            errorMessage:
              !!event.data && isError(event.data)
                ? event.data.message
                : 'Something went wrong with LaunchDarkley',
          });
        },
        updateTrackingIdentity: context => updateTrackingIdentity(context),
        updateOriginationSource: context => {
          // TODO: This action is doing an async operation. actions must be synchronous
          updateOriginationSource(context.funnelPacket?.user.id, FunnelTrack.Waitlist);
        },
        handlePaymentHoldError: context => {
          const payload = {
            extras: {
              userId: context?.funnelPacket?.user.id,
              paymentMethodId: context.funnelPacket?.customPlan.paymentMethodId!,
            },
          };
          trackError(new Error('PaymentHoldError'), payload);
        },
      },
      delays: {
        LISTEN_FOR_PAYMENT_TIMEOUT: 60000,
      },
      guards: {
        isConstellation: _context => {
          return false;
        },
        isUtilityInWaitList: () => false,
        isLmi,
        isNonLmi: (_context, event) => {
          return event.data.lmiStatus === 'notLmi';
        },
        isNonLmiAndUtilityConnect: (context, event) =>
          event.data.lmiStatus === 'notLmi' && context.utilityConnectStatus === 'verified',
        isResuming: context => isNonLmiUser(context) && !!context.queryParams?.resumingUser,
        isBlockSignupWhenResuming: context => {
          const shouldBlockSignups = LDClient.variation(LDFlags.shouldBlockSignups);
          return shouldBlockSignups && isResumingUserInBlockedStates(context, params);
        },
        isSignupUnavailable: context => {
          const shouldBlockSignups = LDClient.variation(LDFlags.shouldBlockSignups);
          const utilityStateAbbreviation = context.utilitySelection?.state.abbreviation!;
          const isBlockedState = BlockedStates(params.id).includes(utilityStateAbbreviation);
          const isBlockedStateForEnergyWell =
            BLOCKED_STATES_FOR_ENERGYWELL.includes(utilityStateAbbreviation);

          return shouldBlockSignups && isEnergyWell(context)
            ? isBlockedStateForEnergyWell
            : isBlockedState;
        },
        isResumingWithLmi: context => !isNonLmiUser(context) && !!context.queryParams?.resumingUser,
        needsDocuSign,
        onlyUcbNY: (_context, event) =>
          event.data.billingExperienceOptions.length === 1 &&
          event.data.billingExperienceOptions.includes('ucb') &&
          LDClient.variation(LDFlags.shouldUseNYLmiNewFlow),
        onlyUcbVAUtility: (context, event) =>
          event.data.billingExperienceOptions.length === 1 &&
          event.data.billingExperienceOptions.includes('ucb') &&
          isUserFromVA(context, params.id),
        onlyUcb: (_context, event) =>
          event.data.billingExperienceOptions.length === 1 &&
          event.data.billingExperienceOptions.includes('ucb'),
        shouldAdvanceOnResumingWithUtilityConnect: context => {
          return (
            context.funnelPacket!.utilityCredential.verificationStatus! === 'correct' &&
            isAddressComplete(context)
          );
        },
        isUanPresent: context =>
          !!context.queryParams?.unparsedQueryParams?.encodedUtilityAccountNumber,
        shouldAdvanceOnResumingWithUtilityAccountNumber: context => {
          return !!context.funnelPacket!.utilityAccount.accountNumber && isAddressComplete(context);
        },
        shouldUploadDocs: context => {
          const funnelId = params.id;
          const nonLmi = context.queryParams?.unparsedQueryParams?.nonLmi;
          const resumingWithLmi = context.queryParams?.resumingUser;
          const lmiProofDocsStates = LDClient.variation(LDFlags.lmiProofDocsState);
          /*
          Need to come up with a cleaner implementation , but for now
          until we remove the waitlist flag this is needed for resuming flow atleast
          */
          if (funnelId === GO_SOLAR_FUNNEL_ID && nonLmi) {
            return false;
          }
          /*
          We check for resuming with LMI together with the LD flag - this is because
          to skip the upload docs screen for states not included in the LD flag.
          This is important because if the query params are not provided, it defaults to false.
          */
          if (resumingWithLmi) {
            return (
              !isNonLmiUser(context) &&
              (lmiProofDocsStates?.includes(context.funnelPacket?.utility.state.abbreviation) ||
                lmiProofDocsStates?.includes(
                  context.queryParams.resumingUser?.packet?.utility.state.abbreviation,
                ))
            );
          }
          // defaults to LD flag
          return lmiProofDocsStates?.includes(context.utilitySelection!.state.abbreviation);
        },
        isWaitlistEnabled: () => false,
        isUserFromOregonLmi: () => false,
        isNjUtility: context => {
          return fetchShowDocusignFlagValue() && isNjUtility(context);
        },
        isResumingNjUtility: context => {
          const resumingUser = context.queryParams?.resumingUser;
          return (
            !!resumingUser &&
            resumingUser?.id &&
            fetchShowDocusignFlagValue() &&
            isNjUtility(context)
          );
        },
        isUserInNonLmiUtility: context => {
          return (
            LDClient.variation(LDFlags.shouldUseNYLmiNewFlow) && isConsolidatedNYUtility(context)
          );
        },
        isUserInDacZone: context =>
          LDClient.variation(LDFlags.shouldUseNYLmiNewFlow) &&
          Boolean(context.nyLmiZoneOptions?.dacZone),
        isUserInEmpowerZone: context =>
          LDClient.variation(LDFlags.shouldUseNYLmiNewFlow) &&
          Boolean(context.nyLmiZoneOptions?.empowerZone),
        /**
         * we need this guard and cannot just use 'isUserInDacZone' because,
         * when a DAC user comes back from docusign, LD doesn't return
         * the correct value for the `shouldUseNYLmiNewFlow` flag.
         * So using `isUserInDacZone` guard will fail.
         * It's okay to ignore the LD flag here because if the LD flag was off,
         * `context.nyLmiZoneOptions.dacZone` will be false at this point.
         */
        isNyDacUser: context => Boolean(context.nyLmiZoneOptions?.dacZone),
        isNyLmiOptionsUnknown: context => {
          return (
            context.funnelPacket?.utility?.state.abbreviation === 'NY' &&
            isNonDacEmpowerZone(context) &&
            !context.nyLmiZoneOptions?.isAddressPresent
          );
        },
        isUserFromNY: context => {
          return (
            LDClient.variation(LDFlags.shouldUseNYLmiNewFlow) && !isFunnelTrackWaitlist(context)
          );
        },
        isUserFromVA: context => {
          return isUserFromVA(context, params.id);
        },
        isPowerSetter: _context => false,
        isMinnesotaLMI: _context => false,
        isMnLmiWithSelfAttestation: _context => false,
        isMnLmiWithAddress: _context => false,
        isMnLmiWithoutAddress: _context => false,
        isUserFromMD,
        isUserFromDE: context =>
          context.utilitySelection?.state.abbreviation === 'DE' ||
          context.funnelPacket?.utility.state.abbreviation === 'DE',
        isUserMissingServiceAddress,
        isEnergyWell: context => {
          return !!(
            (context?.queryParams?.promoCode ||
              context?.queryParams?.unparsedQueryParams?.promoCode ||
              context?.funnelPacket?.user?.signupPromoCode?.code) === PROMO_CODES.ENERGY_WELL_SIGNUP
          );
        },
        isNYUserNeitherDacNorEZ: context =>
          LDClient.variation(LDFlags.shouldUseNYLmiNewFlow) && isNonDacEmpowerZone(context),
        isNYUserLMIZoneOptionUnknown: context => {
          return (
            LDClient.variation(LDFlags.shouldUseNYLmiNewFlow) &&
            isNonDacEmpowerZone(context) &&
            !context.nyLmiZoneOptions?.isAddressPresent
          );
        },
        shouldSkipToLegal: context => {
          return !context.utilityScrapeEligible && context.funnelTrack === FunnelTrack.Waitlist;
        },
        shouldWaitlistSkipPayment: () => false,
        paymentHoldFailed: (_, event) => {
          return event.data.result !== 'success';
        },
        isPaymentTypeCard: context => {
          return context.energywellPaymentMethod?.type === PaymentMethodType.CreditCard;
        },
        isPaymentMethodAlreadySelected: context => {
          return !!context.isUserMissingServiceAddress && !!context.paymentMethod;
        },
        isTelesalesNoPayment: context => {
          const funnel = context.funnelPacket?.user?.originationSource?.funnel;
          return funnel === 'telesalesNoPayment';
        },
        energywellShouldHoldPayment: context => {
          return isEnergyWell(context) && LDClient.variation(LDFlags.energywellShouldHoldPayment);
        },
        shouldDocusignTimeoutRedirectToDashboard: context => {
          const utilities: number[] = [
            UTILITY_IDS['Potomac Edison MD'],
            UTILITY_IDS['Ameren IL'],
            UTILITY_IDS['Commonwealth Edison'],
            UTILITY_IDS['Eversource / NSTAR'],
            UTILITY_IDS['Eversource / WMECO'],
            UTILITY_IDS['National Grid MA'],
          ];
          const utilityId = context.funnelPacket?.utility?.id;
          return utilityId ? utilities.includes(utilityId) : false;
        },
        hasNoUtilityAccountsAdded: context => {
          return (
            needsDocuSign(context) &&
            !!context.funnelPacket?.utilityAccount?.utilityAccountMissingError
          );
        },
        requiresAffirmativeConsent,
      },
      services: {
        billingMachine: billingMachine(),
        docuSignMachine: docuSignMachine(),
        enrollmentMachine: enrollmentMachine().withConfig({
          services: {
            ...(params.enrollment?.services?.fetchBillingExperienceOptions
              ? {
                  fetchBillingExperienceOptions:
                    params.enrollment?.services?.fetchBillingExperienceOptions,
                }
              : {}),
          },
          guards: {
            shouldAdvance: context => {
              return ['verified'].includes(context.utilityConnectStatus!)
                ? true
                : !(
                    LDClient.variation(LDFlags.accountNumberBackup) ||
                    LDClient.variation(LDFlags.billImageUpload)
                  ) && ['timed_out', 'error', 'rejected'].includes(context.utilityConnectStatus!);
            },
            shouldFallBackToAccountNumber: context =>
              LDClient.variation(LDFlags.accountNumberBackup) &&
              ['error', 'timed_out'].includes(context.utilityConnectStatus!),
            shouldFallBackToBillImageUpload: context => {
              const { utilityConnectStatus, billingExperienceOptions } = context;
              if (
                billingExperienceOptions?.includes('acb') &&
                LDClient.variation(LDFlags.ffFeatureEnableBilluploadOnUcFail)
              ) {
                return ['error', 'timed_out', 'rejected'].includes(utilityConnectStatus!);
              }
              return (
                LDClient.variation(LDFlags.billImageUpload) &&
                ['error', 'timed_out', 'rejected'].includes(utilityConnectStatus!)
              );
            },
          },
        }),
        reviewMachine: reviewMachine(),
        lmiReviewMachine: lmiReviewMachine(),
        acuityMachine: acuityMachine(),
        legalMachine: legalMachine().withConfig({
          guards: {
            noTermsNeeded: context => {
              return isEnergyWell(context) || isPowerSetter(context);
            },
          },
          services: {
            ...(params.legal?.services?.signTerms
              ? { signTerms: params.legal?.services?.signTerms }
              : {}),
          },
        }),
        uploadMachine: uploadMachine(),
        uploadContractsMachine: uploadContractsMachine(),
        userMachine: userMachine(),
        utilityMachine: utilityMachine(),
        fetchExistingPlans: async context => {
          const { existingPlans } = await fetchExistingPlans({
            userId: context.funnelPacket!.user.id,
          });
          try {
            await triggerDocuSignAlert(context, params.id);
          } catch (err) {
            trackError(new Error(err.title), {
              extras: { message: err.message || err.title, details: err.details },
            });
          }
          return { existingPlans };
        },
        fetchBillingExperienceOptions: async context => {
          const { billingExperienceOptions } = await fetchBillingExperienceOptions({
            enrollmentDataSourceName: context.enrollmentSelection!,
            preference: context.queryParams.preferredBillingExperience,
            utilityId: context.funnelPacket!.utility.id,
            communitySolarProjectGroupId:
              context.funnelPacket!.utilityAccount.communitySolarProjectGroup?.id,
          });
          return { billingExperienceOptions };
        },
        fetchWaitlistOptions: async context => {
          const { utilityScrapeEligible, funnelTrack } = await fetchWaitlistOptions({
            utilityId: context.funnelPacket!.utility.id,
          });
          return { utilityScrapeEligible, funnelTrack };
        },
        listenForPaymentCreation: async context => {
          const { paymentMethod } = await listenForPayment({
            accountId: context?.funnelPacket?.account.id!,
          });
          return { paymentMethod };
        },
        verifyPaymentHold: async context => {
          const { result, message } = await createPaymentHold({
            accountId: context?.funnelPacket?.account?.id!,
            paymentMethodId:
              context?.energywellPaymentMethod?.id! ||
              context?.funnelPacket?.customPlan?.paymentMethodId!,
            deletePaymentMethodOnFailure: true,
          });
          return { result, message };
        },
        docusignTimeoutRedirect: async context => {
          await redirectToDashboardFromDocusignTimeout({
            userId: context.funnelPacket!.user.id,
          });
        },
        signUpUser: async context => {
          const { plan } = await signUpUser({
            utilityAccountId: context.funnelPacket!.utilityAccount.id,
            planId: context.funnelPacket!.customPlan.id,
            enrollmentDataSourceName:
              context.enrollmentSelection! ||
              context.funnelPacket!.utilityAccount.enrollmentDataSourceName ||
              'none',
            billingExperienceType: context.billingSelection! || 'none',
            track: isFunnelTrackWaitlist(context)
              ? FunnelTrack.Waitlist
              : context.funnelTrack || context.funnelPacket!.utility.funnelTrack,
          });
          return { planId: plan.id };
        },
        refetchFunnelPacket: async context => {
          const funnelPacket = await fetchFunnelPacket({
            userId: context.funnelPacket!.user.id,
            utilityAccountId: context.funnelPacket!.utilityAccount.id,
          });
          return { funnelPacket };
        },
        updateUtilityAccountDetail: async context => {
          const response = await updateUtilityAccountDetail({
            utilityAccountId: context.funnelPacket!.utilityAccount.id,
            requiresAffirmativeConsent: requiresAffirmativeConsent(context),
          });
          return {
            utilityAccountDetailId: response?.utilityAccountDetailId,
          };
        },
        updateTrackingIdentity: async context => {
          const ldFlags = await updateTrackingIdentity(context);
          return { ldFlags };
        },
        sendInstantAlert: async context => {
          const isLmiState = DOCU_SIGN_LMI_STATES.includes(
            context.funnelPacket?.utility.state.abbreviation!,
          );

          const shouldSendInstantAlert = context?.lmiStatus
            ? context.lmiStatus !== 'notLmi' && isLmiState
            : isLmiState;

          const promoCode =
            context.queryParams?.promoCode ||
            context.funnelPacket?.user.signupPromoCode?.code ||
            context.funnelPacket?.user.promoCode;

          if (shouldSendInstantAlert) {
            await sendDocusignInstantAlert({
              utilityAccountId: context.funnelPacket!.utilityAccount.id,
              track: (context.funnelTrack ||
                context.funnelPacket!.utility.funnelTrack) as FunnelTrack,
              userIp: getUserIpFromStore(),
              inviteCode: context.funnelPacket?.inviteCode!,
            });
          }

          if (allowConsolidatedUtilityAccount(promoCode)) {
            const utilityAccountId = context.funnelPacket?.utilityAccount.id!;
            const updateInput = {
              ipAddress: getUserIpFromStore(),
            };
            await updateConsolidatedUtilityAccount(utilityAccountId, updateInput);
          }
        },
      },
    },
  );

export const updateTrackingIdentity = context => {
  const packet = context.funnelPacket || context.queryParams?.resumingUser?.packet;
  const forceWaitlist = context?.forceWaitlist || context?.queryParams?.resumingUser?.forceWaitlist;

  if (packet) {
    return identify({
      email: packet.user.email,
      id: packet.user.id,
      utilityId: packet.utility.id,
      promoCode: packet.user?.promoCode,
      funnelTrack: forceWaitlist ? FunnelTrack.Waitlist : packet.utility.funnelTrack,
      state: packet.utility.state.abbreviation,
    });
  } else {
    return identify({
      utilityId: context.utilitySelection?.id,
      promoCode: context.queryParams?.promoCode,
      funnelTrack: forceWaitlist ? FunnelTrack.Waitlist : context.utilitySelection?.funnelTrack,
    });
  }
};

export const isResumingUserInBlockedStates = (context, params) => {
  const resumingUser = context.queryParams?.resumingUser;
  const utilityStateAbbreviation = resumingUser?.packet?.utility.state.abbreviation;
  const isBlockedState = BlockedStates(params.id).includes(utilityStateAbbreviation);
  const isBlockedStateForEnergyWell =
    BLOCKED_STATES_FOR_ENERGYWELL.includes(utilityStateAbbreviation);

  return !!resumingUser && isEnergyWell(context) ? isBlockedStateForEnergyWell : isBlockedState;
};

const isNewYorkOrNewJersey = (context: SolarFunnelContext) =>
  context.funnelPacket?.utility.state.abbreviation === 'NY' ||
  context.funnelPacket?.utility.state.abbreviation === 'NJ';
const isLmi = (context: SolarFunnelContext) =>
  context.lmiStatus === 'isLmi' || context.lmiStatus === 'maybeLmi';
const isNonLmiUser = context => {
  if (context.queryParams?.resumingUser?.packet) {
    return context.queryParams.resumingUser.packet.nonLmi;
  }
  return true;
};

const isUserFromVA = (context: SolarFunnelContext, funnelId: string) => {
  return (
    ((context.funnelPacket?.utility.id === UTILITY_IDS['Dominion Energy VA'] ||
      context.utilitySelection?.id === UTILITY_IDS['Dominion Energy VA']) &&
      [I_SOLAR_UCB, GO_SOLAR_FUNNEL_ID, COMMUNITY_SOLAR_FUNNEL_ID, BUSINESS].includes(funnelId)) ||
    LDClient.variation(LDFlags.shouldUseVALmiFlow)
  );
};

const isUserFromMD = () => {
  const flagValue =
    LDClient.variation(LDFlags.ffUseGeoCoderForMd) ||
    localStorage.getItem('useGeoCoderForMd') === 'true';
  if (flagValue) localStorage.setItem('useGeoCoderForMd', flagValue);
  return flagValue;
};

const isUserMissingServiceAddress = context => {
  const flagValue = LDClient.variation(LDFlags.enableFallbackOnNoServiceAddress);
  const isServiceAddressCityPresent =
    context.funnelPacket?.utilityAccount?.serviceAddressCity?.length > 0;
  const isServiceAddressStatePresent =
    context.funnelPacket?.utilityAccount?.serviceAddressState?.length > 0;
  const isServiceAddressStreetOnePresent =
    context.funnelPacket?.utilityAccount?.serviceAddressStreetOne?.length > 0;
  const isServiceAddressZipPresent =
    context.funnelPacket?.utilityAccount?.serviceAddressZip?.length > 0;
  const isServiceAddressPresent =
    isServiceAddressCityPresent &&
    isServiceAddressStatePresent &&
    isServiceAddressStreetOnePresent &&
    isServiceAddressZipPresent;

  return flagValue && !isServiceAddressPresent;
};
const isNonDacEmpowerZone = (context: SolarFunnelContext) => {
  return !context.nyLmiZoneOptions?.dacZone && !context.nyLmiZoneOptions?.empowerZone;
};

const triggerDocuSignAlert = async (context: SolarFunnelContext, funnelId) => {
  if (funnelId !== GO_SOLAR_FUNNEL_ID || !isNewYorkOrNewJersey(context)) return;
  const userId = context?.funnelPacket?.user.id!;
  const lmiStatus = context.lmiStatus!;

  const { utilityAccounts } = await fetchExistingUtilityAccounts({
    userId,
  });

  const utilityAccount = utilityAccounts?.find(
    ua => ua.id === context.funnelPacket?.utilityAccount.id,
  );
  /* eslint-disable camelcase */
  if (lmiStatus === 'notLmi') {
    return sendToSegment({
      type: 'non_lmi_user',
      funnelPacket: context.funnelPacket!,
      additional: {
        account_number: utilityAccount?.accountNumber || null,
      },
    });
  }
  return sendToSegment({
    type: 'docusign_lmi_user_signed',
    funnelPacket: context.funnelPacket!,
    additional: {
      account_number: utilityAccount?.accountNumber || null,
      lmiStatus,
    },
  });
  /* eslint-enable camelcase */
};

export const getExternalSystem = (
  context: SolarFunnelContext,
): 'Powersetter' | 'Energywell' | undefined => {
  if (isEnergyWell(context)) {
    return 'Energywell';
  } else if (isPowerSetter(context)) {
    return 'Powersetter';
  }
  return undefined;
};

export const isEnergyWell = (context: SolarFunnelContext | LegalContext | UploadContext) => {
  if ('queryParams' in context) {
    return !!(
      (context?.queryParams?.promoCode ||
        context?.queryParams?.unparsedQueryParams?.promoCode ||
        context?.funnelPacket?.user?.signupPromoCode?.code) === PROMO_CODES.ENERGY_WELL_SIGNUP
    );
  } else {
    return !!(
      context?.funnelPacket?.user?.signupPromoCode?.code === PROMO_CODES.ENERGY_WELL_SIGNUP
    );
  }
};

export const isConsolidatedNYUtility = (context: SolarFunnelContext) => {
  return context.funnelPacket?.utility.id === UTILITY_IDS['Consolidated Edison NY'];
};
export const isFunnelTrackWaitlist = (context: SolarFunnelContext) => {
  const shouldCheckForWaitlistFlow = LDClient.variation(LDFlags.waitlistEnabled);
  return (
    shouldCheckForWaitlistFlow &&
    (context.funnelTrack === FunnelTrack.Waitlist ||
      isBlockedUtility(context?.funnelPacket?.utility.id!))
  );
};

const fetchShowDocusignFlagValue = () => {
  // when returning from docusign , LD isn't able to fetch the flag value immediately this is a workaround
  const flagValue =
    LDClient.variation(LDFlags.showLmiSelfAttestationPostPayment) ||
    localStorage.getItem('showLmiSelfAttestationPostPayment') === 'true';
  if (flagValue) localStorage.setItem('showLmiSelfAttestationPostPayment', flagValue);
  return flagValue;
};

const isNjUtility = context => {
  return (
    context.funnelPacket.utility.state.abbreviation === 'NJ' ||
    context.utilitySelection?.state.abbreviation === 'NJ'
  );
};

export const isBlockedUtility = (utilityId: number): boolean => {
  // Ohio Edison(2349), AEP OH(2342), Toeldo Edison(2531), Eversource / PSNH(2326), Duke Energy OH(2344), Dayton Power & Light(2346), The Illuminating Company(2345)
  const blockedUtilityIds = [2349, 2342, 2531, 2326, 2344, 2346, 2345];

  return blockedUtilityIds.includes(utilityId);
};

export const requiresAffirmativeConsent = (context: SolarFunnelContext | PerchEnergyContext) => {
  return (
    (context.funnelPacket?.utility.state.abbreviation === 'NJ' ||
      context.utilitySelection?.state.abbreviation === 'NJ') &&
    LDClient.variation(LDFlags.ffEnableNJAffirmativeConsent) &&
    Boolean(context.funnelId !== SOLAR_LANDSCAPE_FUNNEL_ID)
  );
};

export const isAddressComplete = (context: SolarFunnelContext) => {
  return (
    !!context.funnelPacket?.utilityAccount?.serviceAddressCity &&
    !!context.funnelPacket?.utilityAccount?.serviceAddressState &&
    !!context.funnelPacket?.utilityAccount?.serviceAddressStreetOne &&
    !!context.funnelPacket?.utilityAccount?.serviceAddressZip
  );
};
